import { ClientError } from 'graphql-request';

import { requestZeusFromBrowser } from '../api/zeusBrowserClient';
import {
  CreateAgentOfferDocumentUploadUrlDocument,
  type CreateAgentOfferDocumentUploadUrlMutation,
} from './__generated__/offer-document-upload.zeus';
import { type OfferDocumentPurpose, type OfferDocumentRef } from './offerDraft';
import { errorCode } from './offerSubmission';

/**
 * Document upload for the offer wizard: Zeus's
 * `agentOfferDocumentUploadUrlCreate` mints a presigned S3 PUT plus an opaque
 * document handle, the browser PUTs the bytes straight to S3, and only the
 * handle travels onward — the submit mutation later attaches it to the offer.
 */
const OFFER_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Contract documents have to be PDFs: a photo of a signature page is not
 * something the Listing Partner can put on paper. The presign accepts images
 * too, so this is the narrower of the two allowlists.
 */
const ALLOWED_TYPES: Record<string, readonly string[]> = {
  'application/pdf': ['pdf'],
};

const fileExtension = (name: string): string => name.split('.').pop()?.toLowerCase() ?? '';

const isAllowedOfferDocumentType = (file: File): boolean =>
  ALLOWED_TYPES[file.type]?.includes(fileExtension(file.name)) ?? false;

export type OfferDocumentUploadResult =
  | { kind: 'uploaded'; document: OfferDocumentRef; submissionId: string }
  /** The upload was refused; the message says why and what to do. */
  | { kind: 'rejected'; message: string }
  /** A transient transport failure — the same file may work on retry. */
  | { kind: 'failed' };

export const uploadOfferDocument = async ({
  file,
  listingId,
  purpose,
  submissionId,
}: {
  /** The file the agent picked; validated against the type/size allowlist. */
  file: File;
  /** The listing the offer targets — uploads are scoped to it server-side. */
  listingId: number;
  /** What the document is; sealed into the handle and drives intake's type. */
  purpose: OfferDocumentPurpose;
  /** Groups one wizard session's files; omitted on the first upload. */
  submissionId: string | null;
}): Promise<OfferDocumentUploadResult> => {
  if (!isAllowedOfferDocumentType(file)) {
    return { kind: 'rejected', message: 'Use a PDF file.' };
  }
  if (file.size === 0) {
    return { kind: 'rejected', message: `${file.name} is empty.` };
  }
  if (file.size > OFFER_DOCUMENT_MAX_BYTES) {
    return { kind: 'rejected', message: 'Files can be up to 10MB.' };
  }
  let data: CreateAgentOfferDocumentUploadUrlMutation;
  try {
    data = await requestZeusFromBrowser(
      CreateAgentOfferDocumentUploadUrlDocument,
      {
        listingId: String(listingId),
        purpose,
        fileName: file.name,
        contentType: file.type,
        // The presigned PUT accepts exactly this many bytes, no more.
        contentLength: file.size,
        ...(submissionId != null ? { submissionId } : {}),
      },
      AbortSignal.timeout(15_000),
    );
  } catch (error) {
    if (error instanceof ClientError && errorCode(error) === 'RATE_LIMITED') {
      return { kind: 'rejected', message: 'Too many uploads — wait a minute and try again.' };
    }
    return { kind: 'failed' };
  }
  const payload = data.agentOfferDocumentUploadUrlCreate;
  if (payload.__typename !== 'AgentOfferDocumentUploadUrl') {
    // The client validated type and size already, so a typed refusal means
    // the request cannot succeed as-is; anything else reads as retriable.
    return payload.__typename === 'InvalidInputError'
      ? { kind: 'rejected', message: 'Check the file type and size, then try again.' }
      : { kind: 'failed' };
  }
  try {
    // The PUT goes straight to S3; the presigned URL binds type and size, so
    // a mismatched body fails here rather than landing in the bucket.
    const response = await fetch(payload.url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
      signal: AbortSignal.timeout(120_000),
    });
    if (!response.ok) {
      return { kind: 'failed' };
    }
  } catch {
    return { kind: 'failed' };
  }
  return {
    kind: 'uploaded',
    document: {
      documentId: payload.document.id,
      fileName: file.name,
      sizeBytes: file.size,
      purpose,
    },
    submissionId: payload.submissionId,
  };
};
